import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import { getTenant } from '../lib/tenant';
import { Check, X, AlertTriangle, Key } from 'lucide-react';
import { API_BASE } from '../lib/api';
import { useFiscalAdapter } from '../lib/fiscal/useFiscalAdapter';

export default function SubscriptionDashboard() {
    const { t } = useTranslation();
    const { adapter } = useFiscalAdapter();
    const isSpain = adapter.countryCode === 'ES';
    const cur = adapter.meta.currencySymbol;
    const taxLabel = adapter.meta.taxLabel;

    const [profile, setProfile] = useState(null);
    const [licenses, setLicenses] = useState([]);
    const [dbPlans, setDbPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isAnnual, setIsAnnual] = useState(true);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        const { user, ownerId } = await getTenant();
        if (!user) return;

        // Team members see the company's (employer's) subscription, not their own.
        const { data: pData } = await supabase.from('profiles').select('*').eq('id', ownerId).single();
        let currentProfile = pData;
        if (!currentProfile) {
            currentProfile = {
                id: ownerId,
                subscription_status: 'trial',
                trial_end_date: new Date(new Date().getTime() + 20 * 24 * 60 * 60 * 1000).toISOString(),
                next_billing_date: null,
                subscription_plan_id: null
            };
        }
        setProfile(currentProfile);

        const { data: lData } = await supabase.from('licenses').select('*').eq('user_id', ownerId);
        if (lData) setLicenses(lData);

        const { data: plansData } = await supabase.from('plans').select('*');
        if (plansData) setDbPlans(plansData);

        setLoading(false);
    };

    const handleSubscribe = async (planName, planObj) => {
        try {
            setLoading(true);
            const { ownerId } = await getTenant();

            // Match the UI plan name with the actual ID from the database.
            // Accent/case-insensitive so "Plan Básico" matches a stored "Plan Basico".
            const matchedPlan = dbPlans.find(
                p => (p.name || '').localeCompare(planName || '', 'es', { sensitivity: 'base' }) === 0
            );
            if (!matchedPlan) {
                throw new Error(t('subdash.planSyncError'));
            }

            // Amount is in cents for Redsys (e.g., 80 EUR = 8000 cents)
            // Use exact annualBilled value or priceMonthly depending on selection
            const amountInCents = Math.round((isAnnual ? planObj.annualBilled : planObj.priceMonthly) * 100);

            const response = await fetch(`${API_BASE}/api/redsys/create-payment`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    plan_id: matchedPlan.id,
                    user_id: ownerId,
                    amount: amountInCents
                })
            });

            const data = await response.json();

            if (!response.ok) throw new Error(data.detail || t('subdash.gatewayError'));

            const form = document.createElement('form');
            form.method = 'POST';
            form.action = 'https://sis-t.redsys.es:25443/sis/realizarPago'; // Entorno de pruebas

            const params = {
                'Ds_SignatureVersion': data.signature_version,
                'Ds_MerchantParameters': data.params_b64,
                'Ds_Signature': data.signature
            };

            Object.entries(params).forEach(([key, value]) => {
                const input = document.createElement('input');
                input.type = 'hidden';
                input.name = key;
                input.value = value;
                form.appendChild(input);
            });

            document.body.appendChild(form);
            form.submit();

        } catch (error) {
            alert(t('subdash.gatewayConnectError', { msg: error.message }));
            setLoading(false);
        }
    };

    if (loading || !profile) return <div className="p-10 flex justify-center text-[#1a4f8b]">{t('subdash.loadingPlans')}</div>;

    const trialEnd = new Date(profile.trial_end_date);
    const daysLeft = Math.ceil((trialEnd - new Date()) / (1000 * 60 * 60 * 24));

    // Plan contracted by this account (null during the trial). Used to mark the
    // "Tu plan actual" card and to render the subscription summary at the top.
    const currentDbPlan = profile.subscription_plan_id
        ? dbPlans.find(p => p.id === profile.subscription_plan_id)
        : null;
    const isCurrentPlan = (uiName) =>
        currentDbPlan && (currentDbPlan.name || '').localeCompare(uiName || '', 'es', { sensitivity: 'base' }) === 0;
    const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('es-ES') : null);

    // Human-readable summary of the account's subscription state.
    const summary = (() => {
        const s = profile.subscription_status;
        if (s === 'trial') {
            return {
                badge: daysLeft > 0 ? t('subdash.badgeTrial') : t('subdash.badgeTrialEnded'),
                badgeClass: daysLeft > 0 ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700',
                title: currentDbPlan ? currentDbPlan.name : t('subdash.trialPeriod'),
                detail: daysLeft > 0
                    ? t('subdash.trialDaysLeft', { count: daysLeft, date: fmtDate(profile.trial_end_date) })
                    : t('subdash.trialEndedDetail'),
            };
        }
        if (s === 'active') {
            return {
                badge: t('subdash.badgeActive'),
                badgeClass: 'bg-green-100 text-green-700',
                title: currentDbPlan ? currentDbPlan.name : t('subdash.planContracted'),
                detail: profile.next_billing_date
                    ? t('subdash.nextRenewal', { date: fmtDate(profile.next_billing_date) })
                    : t('subdash.activeSubscription'),
            };
        }
        return {
            badge: s === 'cancelled' ? t('subdash.badgeCancelled') : t('subdash.badgeExpired'),
            badgeClass: 'bg-red-100 text-red-700',
            title: currentDbPlan ? currentDbPlan.name : t('subdash.noActivePlan'),
            detail: t('subdash.subscriptionInactive'),
        };
    })();

    const plans = [
        {
            name: "Plan Básico",
            subtitle: t('subdash.basicSubtitle'),
            priceMonthly: 8,
            priceAnnual: 6.6,
            annualBilled: 80,
            features: [
                { text: t('subdash.f.clients0to5'), included: true },
                { text: t('subdash.f.users1'), included: true },
                { text: t('subdash.f.products10'), included: true },
                { text: t('subdash.f.digitize5'), included: true },
                { text: t('subdash.f.unlimitedDocs'), included: true },
                { text: isSpain ? t('subdash.f.einvoiceEs') : t('subdash.f.einvoicePk'), included: true },
                { text: t('subdash.f.multiDesign'), included: true },
                { text: t('subdash.f.customEmails'), included: true },
                { text: t('subdash.f.connectedManager'), included: true },
                { text: t('subdash.f.emailSupport'), included: true },
                { text: t('subdash.f.mobileApp'), included: true },
                { text: t('subdash.f.stockManagement'), included: true },
                { text: t('subdash.f.analyticsPanel'), included: false },
                { text: t('subdash.f.docTracking'), included: false },
                { text: t('subdash.f.autoPaymentReminder'), included: false },
                { text: t('subdash.f.bulkSend'), included: false },
                { text: t('subdash.f.recurringExpenses'), included: false },
                { text: t('subdash.f.recurringInvoices'), included: false },
                { text: t('subdash.f.bankReconciliation'), included: false, badgeAfter: t('subdash.comingSoon') },
                { text: t('subdash.f.bankRemittances'), included: false },
                { text: t('subdash.f.customSenderEmail'), included: false, badgeAfter: t('subdash.comingSoon') },
                { text: t('subdash.f.pdfSplit'), included: false },
                { text: t('subdash.f.shopifyIntegration'), included: false, badge: t('subdash.badgeNew'), badgeColor: "bg-orange-500" },
                { text: t('subdash.f.billinIntegration'), included: false, badge: t('subdash.badgeBeta'), badgeColor: "bg-blue-500" }
            ]
        },
        {
            name: "Plan Pro",
            subtitle: t('subdash.proSubtitle'),
            priceMonthly: 15,
            priceAnnual: 12.5,
            annualBilled: 150,
            popular: true,
            features: [
                { text: t('subdash.f.clients6to50'), included: true },
                { text: t('subdash.f.users3'), included: true },
                { text: t('subdash.f.products50'), included: true },
                { text: t('subdash.f.digitize50'), included: true },
                { text: t('subdash.f.unlimitedDocs'), included: true },
                { text: isSpain ? t('subdash.f.einvoiceEs') : t('subdash.f.einvoicePk'), included: true },
                { text: t('subdash.f.multiDesign'), included: true },
                { text: t('subdash.f.customEmails'), included: true },
                { text: t('subdash.f.connectedManager'), included: true },
                { text: t('subdash.f.emailChatSupport'), included: true },
                { text: t('subdash.f.mobileApp'), included: true },
                { text: t('subdash.f.stockManagement'), included: true },
                { text: t('subdash.f.analyticsPanel'), included: true },
                { text: t('subdash.f.docTracking'), included: true },
                { text: t('subdash.f.autoPaymentReminder'), included: true },
                { text: t('subdash.f.bulkSend'), included: true },
                { text: t('subdash.f.recurringExpenses'), included: true },
                { text: t('subdash.f.recurringInvoices'), included: true },
                { text: t('subdash.f.bankReconciliation'), included: false, badgeAfter: t('subdash.comingSoon') },
                { text: t('subdash.f.bankRemittances'), included: false },
                { text: t('subdash.f.customSenderEmail'), included: false, badgeAfter: t('subdash.comingSoon') },
                { text: t('subdash.f.pdfSplit'), included: false },
                { text: t('subdash.f.shopifyIntegration'), included: false, badge: t('subdash.badgeNew'), badgeColor: "bg-orange-500" },
                { text: t('subdash.f.billinIntegration'), included: false, badge: t('subdash.badgeBeta'), badgeColor: "bg-blue-500" }
            ]
        },
        {
            name: "Plan Ilimitado",
            subtitle: t('subdash.unlimitedSubtitle'),
            priceMonthly: 24,
            priceAnnual: 20,
            annualBilled: 240,
            features: [
                { text: t('subdash.f.unlimitedClients'), included: true },
                { text: t('subdash.f.users5'), included: true },
                { text: t('subdash.f.moreUsersContact'), included: true },
                { text: t('subdash.f.unlimitedProducts'), included: true },
                { text: t('subdash.f.digitize250'), included: true },
                { text: t('subdash.f.unlimitedDocs'), included: true },
                { text: isSpain ? t('subdash.f.einvoiceEs') : t('subdash.f.einvoicePk'), included: true },
                { text: t('subdash.f.multiDesign'), included: true },
                { text: t('subdash.f.customEmails'), included: true },
                { text: t('subdash.f.connectedManager'), included: true },
                { text: t('subdash.f.emailChatPhoneSupport'), included: true },
                { text: t('subdash.f.mobileApp'), included: true },
                { text: t('subdash.f.stockManagement'), included: true },
                { text: t('subdash.f.analyticsPanel'), included: true },
                { text: t('subdash.f.docTracking'), included: true },
                { text: t('subdash.f.autoPaymentReminder'), included: true },
                { text: t('subdash.f.bulkSend'), included: true },
                { text: t('subdash.f.recurringExpenses'), included: true },
                { text: t('subdash.f.recurringInvoices'), included: true },
                { text: t('subdash.f.bankReconciliation'), included: false, badgeAfter: t('subdash.comingSoon') },
                { text: t('subdash.f.bankRemittances'), included: true },
                { text: t('subdash.f.customSenderEmail'), included: false, badgeAfter: t('subdash.comingSoon') },
                { text: t('subdash.f.pdfSplit'), included: true },
                { text: t('subdash.f.shopifyIntegration'), included: true, badge: t('subdash.badgeNew'), badgeColor: "bg-orange-500" },
                { text: t('subdash.f.billinIntegration'), included: true, badge: t('subdash.badgeBeta'), badgeColor: "bg-blue-500" }
            ]
        }
    ];

    return (
        <div className="bg-[#f8fafc] min-h-screen pb-20">
            {profile.subscription_status === 'trial' && (
                <div className="bg-[#1a4f8b] text-white text-center py-3 px-4 flex items-center justify-center gap-2 text-sm font-medium">
                    <AlertTriangle className="w-4 h-4 text-[#fca311]" />
                    {daysLeft > 0
                        ? t('subdash.trialBannerActive', { count: daysLeft, date: fmtDate(profile.trial_end_date) })
                        : t('subdash.trialBannerEnded')}
                </div>
            )}

            <div className="max-w-7xl mx-auto px-4 pt-12">
                {/* Current subscription summary — visible from the moment the account is created */}
                <div className="max-w-3xl mx-auto mb-12 bg-white rounded-2xl border border-gray-200 shadow-md p-6 flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="flex-grow">
                        <div className="flex items-center gap-3 mb-1">
                            <span className={`text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide ${summary.badgeClass}`}>
                                {summary.badge}
                            </span>
                            <h2 className="text-lg font-bold text-[#1d2d3d]">{summary.title}</h2>
                        </div>
                        <p className="text-sm text-[#6d7d8b]">{summary.detail}</p>
                    </div>
                    {profile.subscription_status === 'trial' && daysLeft > 0 && (
                        <div className="flex-shrink-0 text-center bg-[#f8fafc] border border-gray-200 rounded-xl px-5 py-3">
                            <div className="text-3xl font-bold text-[#1a4f8b] leading-none">{daysLeft}</div>
                            <div className="text-[11px] font-semibold text-[#6d7d8b] uppercase tracking-wide mt-1">
                                {t('subdash.daysRemaining', { count: daysLeft })}
                            </div>
                        </div>
                    )}
                </div>

                {/* Toggle Switch */}
                <div className="flex flex-col items-center justify-center mb-12">
                    <div className="flex items-center gap-3">
                        <span className="text-xs font-bold text-orange-500 tracking-wide">{t('subdash.save17')}</span>
                        <span className={`text-sm font-bold ${isAnnual ? 'text-[#1d2d3d]' : 'text-gray-400'}`}>{t('subdash.annual')}</span>

                        <button
                            className={`w-14 h-7 rounded-full relative transition-colors ${isAnnual ? 'bg-[#1a4f8b]' : 'bg-gray-300'}`}
                            onClick={() => setIsAnnual(!isAnnual)}
                        >
                            <div className={`w-5 h-5 bg-white rounded-full absolute top-1 transition-transform ${isAnnual ? 'left-1' : 'left-8'}`} />
                        </button>

                        <span className={`text-sm font-bold ${!isAnnual ? 'text-[#1d2d3d]' : 'text-gray-400'}`}>{t('subdash.monthly')}</span>
                    </div>
                </div>

                {/* Pricing Cards */}
                <div className="flex flex-col lg:flex-row items-stretch justify-center gap-6 lg:gap-0 mt-8">
                    {plans.map((plan, index) => {
                        const current = isCurrentPlan(plan.name);
                        return (
                        <div
                            key={plan.name}
                            className={`
                                relative bg-white rounded-2xl flex flex-col
                                ${current ? 'border-2 border-green-500 shadow-2xl' : plan.popular ? 'border-2 border-blue-200 shadow-2xl' : 'border border-gray-200 shadow-md mt-4 lg:mt-6 mb-4 lg:mb-6'}
                                ${plan.popular ? 'lg:scale-105 z-10 lg:-mx-2' : 'z-0'}
                                w-full lg:w-[340px] flex-shrink-0
                            `}
                        >
                            {current ? (
                                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-green-500 text-white font-bold px-4 py-1 rounded-full text-xs">
                                    {t('subdash.yourCurrentPlan')}
                                </div>
                            ) : plan.popular && (
                                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-blue-100 text-blue-700 font-bold px-4 py-1 rounded-full text-xs">
                                    {t('subdash.mostPopular')}
                                </div>
                            )}

                            <div className="p-8 pb-6 border-b border-gray-100 flex-grow-0">
                                <h3 className="text-xl font-bold text-[#1d2d3d] font-serif">{plan.name}</h3>
                                <p className="text-xs text-gray-500 mt-1 h-4">{plan.subtitle}</p>

                                <div className="mt-6 flex items-start text-blue-600">
                                    <span className="text-5xl font-light tracking-tighter">
                                        {(isAnnual ? plan.priceAnnual : plan.priceMonthly).toString().replace('.', ',')}
                                    </span>
                                    <div className="flex flex-col ml-1 mt-1">
                                        <span className="font-bold text-sm">{cur}/{t('subdash.perMonth')}</span>
                                        <span className="text-[10px] font-bold text-blue-600 mt-0.5">{t('subdash.withoutTax', { tax: taxLabel })}</span>
                                    </div>
                                </div>
                                <p className="text-[10px] text-gray-400 mt-2 h-4">
                                    {isAnnual
                                        ? t('subdash.billedAnnually', { amount: plan.annualBilled, cur, tax: taxLabel })
                                        : t('subdash.billedMonthly', { amount: plan.priceMonthly * 12, cur, tax: taxLabel })}
                                </p>
                            </div>

                            <div className="p-8 pt-6 flex-grow">
                                <ul className="space-y-4">
                                    {plan.features.map((feature, i) => (
                                        <li key={i} className="flex items-start gap-3 text-xs text-[#1d2d3d]">
                                            {feature.included ? (
                                                <Check className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" strokeWidth={3} />
                                            ) : (
                                                <X className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" strokeWidth={3} />
                                            )}
                                            <span className="leading-tight">
                                                {feature.badge && (
                                                    <span className={`${feature.badgeColor} text-white text-[9px] font-bold px-1.5 py-0.5 rounded mr-1.5 uppercase`}>
                                                        {feature.badge}
                                                    </span>
                                                )}
                                                {feature.text}
                                                {feature.badgeAfter && (
                                                    <span className="ml-1.5 bg-amber-100 text-amber-700 text-[9px] font-bold px-1.5 py-0.5 rounded uppercase align-middle">
                                                        {feature.badgeAfter}
                                                    </span>
                                                )}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="p-8 pt-0 mt-auto">
                                {current && profile.subscription_status === 'active' ? (
                                    <button
                                        disabled
                                        className="w-full py-3.5 rounded-full font-bold text-white bg-green-500 text-sm shadow-md cursor-default"
                                    >
                                        {t('subdash.activePlan')}
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => handleSubscribe(plan.name, plan)}
                                        disabled={loading}
                                        className="w-full py-3.5 rounded-full font-bold text-white bg-[#fca311] hover:bg-orange-500 transition-colors text-sm shadow-md disabled:opacity-60"
                                    >
                                        {loading ? t('subdash.redirectingToPayment') : current ? t('subdash.renewPlan') : t('subdash.subscribeNow')}
                                    </button>
                                )}
                                <p className="text-center text-[10px] text-gray-400 mt-2">
                                    {t('subdash.securePayment')}
                                </p>
                            </div>
                        </div>
                        );
                    })}
                </div>

                {/* Licenses Display Section */}
                {licenses.length > 0 && (
                    <div className="mt-24 max-w-4xl mx-auto">
                        <h3 className="text-2xl font-bold text-[#1d2d3d] mb-6">{t('subdash.activeLicenses')}</h3>
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                            {licenses.map(lic => (
                                <div key={lic.id} className="p-6 border-b last:border-0 flex justify-between items-center bg-[#f8fafc]">
                                    <div className="flex items-center gap-4">
                                        <div className="bg-blue-100 p-3 rounded-full"><Key className="w-6 h-6 text-[#1a4f8b]" /></div>
                                        <div>
                                            <p className="font-bold text-[#1d2d3d] font-mono text-lg tracking-wider">{lic.license_key}</p>
                                            <p className="text-sm text-[#6d7d8b]">{t('subdash.validUntil', { date: new Date(lic.expires_at).toLocaleDateString() })}</p>
                                        </div>
                                    </div>
                                    <div className="bg-green-100 text-green-800 text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-wide">
                                        {t('subdash.active')}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
